import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, limit, getDocs, startAfter } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';
import store from '../../core/productStore';
import { loadSeen } from '../../core/seenProducts';
import { getTopCategories, scoreProduct } from '../../core/taste';
import { catQueryValues } from '../../core/categoryUtils';
import SearchBar from '../../components/SearchBar';
import ProductCard from '../../components/ProductCard';
import categories from '../category/categories';

const BATCH = 20;

// Canonical slug → Firestore category value
const SLUG_TO_CAT = {
  vetements:    'clothing',
  electronique: 'electronics',
  chaussures:   'shoes',
  'jeux-video': 'gaming',
  laptops:      'laptops',
  cameras:      'cameras',
  maison:       'home',
  beaute:       'beauty',
  sport:        'sports',
  bijoux:       'jewelry',
  montres:      'watches',
  jouets:       'toys',
  sante:        'health',
  musique:      'music',
  animaux:      'pets',
  bebe:         'baby',
  livres:       'books',
  auto:         'automotive',
  voyage:       'travel',
  jardinage:    'gardening',
  bricolage:    'tools',
  art:          'art',
  sacs:         'bags',
  autre:        'other',
};

// Default order for new users
const DEFAULT_SLUGS = ['vetements', 'electronique', 'jeux-video', 'chaussures', 'maison', 'beaute'];

const _warm = [];
function warmImages(products) {
  products.forEach(p => {
    const url = p.imageUrl || p.image;
    if (!url) return;
    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.src = url;
    _warm.push(img);
  });
}

const CSS = `
  .prod-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 0 14px 14px;
  }
  @media (min-width: 600px) { .prod-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (min-width: 900px) { .prod-grid { grid-template-columns: repeat(4, 1fr); } }

  .hrow {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 2px 14px 12px;
    scrollbar-width: none;
  }
  .hrow::-webkit-scrollbar { display: none; }

  .disc-card { transition: box-shadow 0.18s, transform 0.18s; cursor: pointer; }
  .disc-card:hover { box-shadow: 0 8px 24px rgba(24,119,242,0.15) !important; transform: translateY(-3px); }
  .disc-card:active { transform: translateY(0) scale(0.98); }
`;

function dedupe(list) {
  const seen = new Set();
  return list.filter(p => {
    const k = p._col + p.id;
    return seen.has(k) ? false : (seen.add(k), true);
  });
}

function sortProducts(list) {
  const seenSet = loadSeen();
  const score = p => {
    const alreadySeen = seenSet.has(`${p._col}:${p.id}`) ? -10000 : 0;
    // likes * 4 rewards popular products, views * 1 penalizes overexposed ones
    return (p.likes || 0) * 4 - (p.views || 0) * 1 + (p.isCreator ? 8 : 0) + alreadySeen;
  };
  return [...list].sort((a, b) => score(b) - score(a));
}

export default function AccueilPage() {
  const navigate = useNavigate();
  const cached   = store.get();

  const [products, setProducts] = useState(() => sortProducts(cached));
  const [loading,  setLoading]  = useState(cached.length === 0);
  const [search,   setSearch]   = useState('');
  const [topCats,  setTopCats]  = useState([]);

  const busy     = useRef(false);
  const done     = useRef(false);
  const curEbay  = useRef(null);
  const curUser  = useRef(null);
  const prodsRef = useRef(products);
  const sentinel = useRef(null);

  useEffect(() => { setTopCats(getTopCategories()); }, []);

  const setProds = (list) => { prodsRef.current = list; setProducts(list); };

  const fetchMore = useCallback(async (reset = false) => {
    if (busy.current)           return;
    if (!reset && done.current) return;
    busy.current = true;
    if (reset) {
      curEbay.current = null; curUser.current = null;
      done.current = false;   setLoading(true);
    }
    try {
      const ebayQ = [collection(db, 'ebay_products'), where('available', '==', true), limit(BATCH)];
      const userQ = [collection(db, 'user_products'), limit(BATCH)];
      if (!reset && curEbay.current) ebayQ.push(startAfter(curEbay.current));
      if (!reset && curUser.current) userQ.push(startAfter(curUser.current));

      const [ebaySnap, userSnap] = await Promise.all([
        getDocs(query(...ebayQ)),
        getDocs(query(...userQ)),
      ]);

      if (ebaySnap.docs.length) curEbay.current = ebaySnap.docs.at(-1);
      if (userSnap.docs.length) curUser.current  = userSnap.docs.at(-1);
      done.current = ebaySnap.docs.length < BATCH && userSnap.docs.length < BATCH;

      const batch = [
        ...userSnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'user_products',  isCreator: true  })),
        ...ebaySnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'ebay_products', isCreator: false })),
      ];
      store.add(batch);
      warmImages(batch);

      if (reset) {
        setProds(sortProducts(dedupe(batch)));
      } else {
        const keys  = new Set(prodsRef.current.map(p => p._col + p.id));
        const fresh = batch.filter(p => !keys.has(p._col + p.id));
        if (fresh.length) setProds(dedupe([...prodsRef.current, ...fresh]));
      }
    } catch (e) { console.error(e); }
    finally { busy.current = false; setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { if (cached.length === 0) fetchMore(true); }, []); // eslint-disable-line

  const check = useCallback(() => {
    if (busy.current || done.current) return;
    const el = sentinel.current;
    if (!el) return;
    if (el.getBoundingClientRect().top - window.innerHeight < 1500) fetchMore(false);
  }, [fetchMore]);

  useEffect(() => { check(); }, [products.length]); // eslint-disable-line

  useEffect(() => {
    let t = null;
    const onScroll = () => { if (t) return; t = setTimeout(() => { t = null; check(); }, 100); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [check]);

  // Personalized "For You" — least viewed + matching user taste
  const seenSet = loadSeen();
  const forYou = [...products]
    .filter(p => !seenSet.has(`${p._col}:${p.id}`))
    .map(p => ({
      ...p,
      _score: scoreProduct(p, topCats)          // taste match (0–100)
            + (p.likes || 0) * 4                // reward popular
            - (p.views || 0) * 0.5              // lightly penalize overexposed
            + (p.isCreator ? 8 : 0),            // boost creator products
    }))
    .sort((a, b) => b._score - a._score)
    .slice(0, 20);

  // Row order: user's top categories first, then defaults
  const rowSlugs = (() => {
    if (topCats.length >= 2) {
      const catToSlug = {};
      categories.forEach(c => { catToSlug[SLUG_TO_CAT[c.slug] || ''] = c.slug; });
      const preferred = topCats
        .map(cat => catToSlug[cat])
        .filter(Boolean)
        .slice(0, 3);
      const rest = DEFAULT_SLUGS.filter(s => !preferred.includes(s));
      return [...preferred, ...rest].slice(0, 6);
    }
    return DEFAULT_SLUGS;
  })();

  const filtered = search
    ? products.filter(p => (p.title || '').toLowerCase().includes(search.toLowerCase()))
    : products;

  return (
    <div style={s.page}>
      <style>{CSS}</style>

      {/* Search */}
      <div style={s.searchWrap}>
        <SearchBar onSearch={setSearch} />
      </div>

      {search ? (
        <>
          <SectionHeader
            title={`Results for "${search}"`}
            sub={`${filtered.length} item${filtered.length !== 1 ? 's' : ''} found`}
          />
          {filtered.length === 0
            ? <EmptySearch />
            : <div className="prod-grid">
                {filtered.map(p => <ProductCard key={p._col + p.id} product={p} />)}
              </div>
          }
        </>
      ) : (
        <>
          {/* ── For You ── */}
          <SectionHeader
            title={topCats.length > 0 ? '✦ Picked for You' : '✦ Trending Now'}
            sub={topCats.length > 0 ? 'Based on your taste' : 'Most popular right now'}
            onSeeAll={() => navigate('/explore')}
            accent
          />
          <div className="hrow">
            {loading
              ? Array.from({ length: 7 }, (_, i) => <BigSkeleton key={i} />)
              : forYou.map(p => <BigCard key={p._col + p.id} p={p} navigate={navigate} />)
            }
          </div>

          <Divider />

          {/* ── Category rows ── */}
          {rowSlugs.map((slug, i) => {
            const cat    = categories.find(c => c.slug === slug);
            const catVal = SLUG_TO_CAT[slug];
            if (!cat || !catVal) return null;
            return (
              <React.Fragment key={slug}>
                <CategoryRow
                  cat={cat}
                  catVal={catVal}
                  onSeeAll={() => navigate(`/category/${slug}`)}
                  navigate={navigate}
                />
                {i < rowSlugs.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}

          <Divider />

          {/* ── All Products ── */}
          <SectionHeader title="All Products" sub={`${products.length}+ items`} />
          {loading
            ? <div className="prod-grid">{Array.from({ length: 12 }, (_, i) => <GridSkel key={i} />)}</div>
            : <div className="prod-grid">{products.map(p => <ProductCard key={p._col + p.id} product={p} />)}</div>
          }
        </>
      )}

      <div ref={sentinel} style={{ height: 1 }} />
      <div style={{ height: 80 }} />
    </div>
  );
}

/* ── Category Row — fetches its own data from Firebase ── */
function CategoryRow({ cat, catVal, onSeeAll, navigate }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [ebaySnap, userSnap] = await Promise.all([
          getDocs(query(collection(db, 'ebay_products'), where('category', '==', catVal), where('available', '==', true), limit(12))),
          getDocs(query(collection(db, 'user_products'), where('category', 'in', catQueryValues(catVal)), limit(5))),
        ]);
        if (cancelled) return;
        const list = [
          ...userSnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'user_products',  isCreator: true  })),
          ...ebaySnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'ebay_products', isCreator: false })),
        ];
        // Discovery score: likes boost a product up, views push it down
        // High likes = surfaces even with views. Fresh + 0 views also surfaces.
        const score = p => (p.likes || 0) * 4 - (p.views || 0) * 1 + (p.isCreator ? 8 : 0);
        list.sort((a, b) => score(b) - score(a));
        setItems(list);
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [catVal]);

  return (
    <>
      <SectionHeader
        title={`${cat.emoji}  ${cat.label}`}
        sub="Scroll to explore"
        onSeeAll={onSeeAll}
      />
      <div className="hrow">
        {loading
          ? Array.from({ length: 7 }, (_, i) => <SmallSkeleton key={i} />)
          : items.length === 0
            ? <p style={s.emptyRow}>No products yet in this category</p>
            : <>
                {items.map(p => <SmallCard key={p._col + p.id} p={p} navigate={navigate} />)}
                <SeeAllTile onClick={onSeeAll} />
              </>
        }
      </div>
    </>
  );
}

/* ── Section header ── */
function SectionHeader({ title, sub, onSeeAll, accent }) {
  return (
    <div style={sh.wrap}>
      <div>
        <h2 style={{ ...sh.title, color: accent ? '#1877F2' : '#1C1E21' }}>{title}</h2>
        {sub && <p style={sh.sub}>{sub}</p>}
      </div>
      {onSeeAll && <button style={sh.btn} onClick={onSeeAll}>See all</button>}
    </div>
  );
}
const sh = {
  wrap:  { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '18px 14px 8px' },
  title: { fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px' },
  sub:   { fontSize: 11.5, color: '#8A8D91', marginTop: 2 },
  btn:   { background: 'none', border: 'none', color: '#1877F2', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
};

/* ── Divider ── */
function Divider() {
  return <div style={{ height: 8, background: '#E4E6EA', margin: '8px 0' }} />;
}

/* ── Big card (For You row) ── */
function BigCard({ p, navigate }) {
  const [imgErr, setImgErr] = useState(false);
  const image = (!imgErr && (p.imageUrl || p.image)) || null;
  const price = p.price ? `$${parseFloat(p.price).toFixed(0)}` : null;
  const title = p.title || '';
  const open  = () => navigate(`/product/${p._col || 'ebay_products'}/${p.id}`, { state: { product: p } });

  return (
    <div className="disc-card" style={bc.card} onClick={open}>
      <div style={bc.imgWrap}>
        {image
          ? <img src={image} alt={title} style={bc.img} referrerPolicy="no-referrer" onError={() => setImgErr(true)} />
          : <NoImg size={170} />
        }
        {p.isCreator && <span style={bc.creatorBadge}>✦</span>}
        {price && <span style={bc.priceBadge}>{price}</span>}
      </div>
      <div style={bc.info}>
        <p style={bc.title}>{title.length > 42 ? title.slice(0, 42) + '…' : title}</p>
        {(p.likes || 0) > 0 && <span style={bc.likes}>♥ {p.likes}</span>}
      </div>
    </div>
  );
}
const bc = {
  card:        { width: 172, flexShrink: 0, background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #E4E6EA', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  imgWrap:     { position: 'relative', width: 172, height: 172, background: '#F0F2F5', overflow: 'hidden' },
  img:         { width: '100%', height: '100%', objectFit: 'cover' },
  creatorBadge:{ position: 'absolute', top: 8, left: 8, background: '#1877F2', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: 99, padding: '2px 7px' },
  priceBadge:  { position: 'absolute', bottom: 8, right: 8, background: 'rgba(255,255,255,0.94)', color: '#1877F2', fontSize: 12, fontWeight: 800, borderRadius: 8, padding: '3px 8px', border: '1px solid #d0e4fd' },
  info:        { padding: '9px 10px 11px', display: 'flex', flexDirection: 'column', gap: 4 },
  title:       { fontSize: 11.5, fontWeight: 500, color: '#1C1E21', lineHeight: 1.35 },
  likes:       { fontSize: 11, color: '#BCC0C4', fontWeight: 600 },
};

/* ── Small card (category rows) ── */
function SmallCard({ p, navigate }) {
  const [imgErr, setImgErr] = useState(false);
  const image = (!imgErr && (p.imageUrl || p.image)) || null;
  const price = p.price ? `$${parseFloat(p.price).toFixed(0)}` : null;
  const title = p.title || '';
  const open  = () => navigate(`/product/${p._col || 'ebay_products'}/${p.id}`, { state: { product: p } });

  return (
    <div className="disc-card" style={sc.card} onClick={open}>
      <div style={sc.imgWrap}>
        {image
          ? <img src={image} alt={title} style={sc.img} referrerPolicy="no-referrer" onError={() => setImgErr(true)} />
          : <NoImg size={132} />
        }
      </div>
      <div style={sc.info}>
        <p style={sc.title}>{title.length > 32 ? title.slice(0, 32) + '…' : title}</p>
        {price && <span style={sc.price}>{price}</span>}
      </div>
    </div>
  );
}
const sc = {
  card:    { width: 132, flexShrink: 0, background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #E4E6EA', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  imgWrap: { width: 132, height: 132, background: '#F0F2F5', overflow: 'hidden' },
  img:     { width: '100%', height: '100%', objectFit: 'cover' },
  info:    { padding: '7px 9px 10px', display: 'flex', flexDirection: 'column', gap: 3 },
  title:   { fontSize: 11, fontWeight: 500, color: '#1C1E21', lineHeight: 1.3 },
  price:   { fontSize: 13, fontWeight: 800, color: '#1877F2' },
};

/* ── "See all" tile ── */
function SeeAllTile({ onClick }) {
  return (
    <div style={sa.tile} onClick={onClick}>
      <span style={sa.arrow}>→</span>
      <span style={sa.label}>See all</span>
    </div>
  );
}
const sa = {
  tile:  { width: 132, height: 176, flexShrink: 0, background: '#E7F0FD', borderRadius: 12, border: '1.5px solid #c5d9f9', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.15s' },
  arrow: { fontSize: 24, color: '#1877F2' },
  label: { fontSize: 12, fontWeight: 700, color: '#1877F2' },
};

/* ── No image placeholder ── */
function NoImg({ size }) {
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.2), opacity: 0.18, background: '#F0F2F5' }}>
      📷
    </div>
  );
}

/* ── Empty search ── */
function EmptySearch() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 8 }}>
      <span style={{ fontSize: 40, opacity: 0.4 }}>🔍</span>
      <p style={{ fontSize: 16, fontWeight: 600, color: '#65676B' }}>No results</p>
      <p style={{ fontSize: 13, color: '#BCC0C4' }}>Try a different keyword</p>
    </div>
  );
}

/* ── Skeletons ── */
const shimmer = {
  background: 'linear-gradient(110deg, #f0f2f5 30%, #e2e5ea 50%, #f0f2f5 70%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
};
function BigSkeleton() {
  return (
    <div style={{ width: 172, flexShrink: 0, borderRadius: 14, overflow: 'hidden', background: '#fff', border: '1px solid #E4E6EA' }}>
      <div style={{ ...shimmer, width: 172, height: 172 }} />
      <div style={{ padding: '9px 10px 11px' }}>
        <div style={{ ...shimmer, height: 8, borderRadius: 4, width: '85%' }} />
        <div style={{ ...shimmer, height: 8, borderRadius: 4, width: '50%', marginTop: 7 }} />
      </div>
    </div>
  );
}
function SmallSkeleton() {
  return (
    <div style={{ width: 132, flexShrink: 0, borderRadius: 12, overflow: 'hidden', background: '#fff', border: '1px solid #E4E6EA' }}>
      <div style={{ ...shimmer, width: 132, height: 132 }} />
      <div style={{ padding: '7px 9px 10px' }}>
        <div style={{ ...shimmer, height: 7, borderRadius: 4, width: '90%' }} />
        <div style={{ ...shimmer, height: 7, borderRadius: 4, width: '45%', marginTop: 6 }} />
      </div>
    </div>
  );
}
function GridSkel() {
  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', background: '#fff', border: '1px solid #E4E6EA' }}>
      <div style={{ ...shimmer, width: '100%', aspectRatio: '1/1' }} />
      <div style={{ padding: '10px 12px 13px' }}>
        <div style={{ ...shimmer, height: 9, borderRadius: 5, width: '82%' }} />
        <div style={{ ...shimmer, height: 9, borderRadius: 5, width: '50%', marginTop: 7 }} />
      </div>
    </div>
  );
}

const s = {
  page:       { background: '#F0F2F5', minHeight: '100vh' },
  searchWrap: { padding: '14px 14px 6px' },
  emptyRow:   { fontSize: 12, color: '#BCC0C4', padding: '20px 0', fontStyle: 'italic' },
};
